import { ChangeEvent, useCallback, useState } from 'react';
import { getEmployeeList } from 'src/apis/employeeApi';
import { BRDiv } from 'src/components/common/common';
import { CodeForm } from 'src/components/common/form/CodeForm';
import { DropDownForm } from 'src/components/common/form/DropdownForm';
import { SingleDateForm } from 'src/components/common/form/SingleDateForm';
import { TimeForm } from 'src/components/common/form/TimeForm';
import { useInfoFormEvents } from 'src/hooks/useInfoFormEvents';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import useUiuxStore from 'src/store/useUiuxStore';
import { InfoCodeSearchFieldConfig } from 'src/types/info-form';
import { defaultTQC2010_R01InfoDto, TQC2010_R01InfoDto } from './Dto_TQC2010_R01';
import { useTQC2010_R01Store } from './Store_TQC2010_R01';
import { formatDate } from 'src/utils/string/stringUtil';

interface Props {
	defaultTQC2010_R01InfoRow?: TQC2010_R01InfoDto;
}

export const Info_TQC2010_R01 = ({
	defaultTQC2010_R01InfoRow = defaultTQC2010_R01InfoDto,
}: Props) => {
	const { editTQC2010_R01, setEditTQC2010_R01 } = useTQC2010_R01Store();
	const { compCdList, workProcessList, inspectionTypeList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const handleInspectorFocusId = 'inspectorEmp_search';
	const noopChange = useCallback(() => {}, []);
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'inspectorEmp',
			category: '검사자',
			focusId: handleInspectorFocusId,
			responseMapping: {
				empNo: 'inspectorEmp',
				empNm: 'inspectorEmpNm',
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getEmployeeList('all');
				return employeeResponse.data.data.find((value) => value.empNo === code);
			},
		},
	];

	const { getFieldHandlers } = useInfoFormEvents({
		editData: editTQC2010_R01,
		setEditData: setEditTQC2010_R01,
		defaultData: defaultTQC2010_R01InfoRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'TQC2010_R01',
	});

	const handleChange = useCallback((
		e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLTextAreaElement>,
	) => {
		const { name, value } = e.target;
		if (!editTQC2010_R01) return;
		setEditTQC2010_R01({ ...editTQC2010_R01, [name]: value });
	}, [editTQC2010_R01, setEditTQC2010_R01]);

	const [inspectDtVisible, setInspectDtVisible] = useState(false);
	const [inspectDtPickerPos, setInspectDtPickerPos] = useState({
		top: 0,
		left: 0,
		height: 0,
	});

	return (
		<div className="w-full h-full flex flex-wrap content-start pt-[12px] pl-[12px] pr-[12px] pb-[6px] overflow-y-auto border border-gray-300 bg-white">
			<DropDownForm
				title="사업장"
				value={editTQC2010_R01?.compCd || ''}
				name="compCd"
				initLabelLess
				data={compCdList}
				editable={false}
				onChange={handleChange}
			/>
			<DropDownForm
				title="작업공정"
				value={editTQC2010_R01?.workProcess || ''}
				name="workProcess"
				initLabelLess
				data={workProcessList}
				editable={false}
				onChange={handleChange}
			/>
			<CodeForm
				label="작업품목"
				value={editTQC2010_R01?.itemCd || ''}
				custNmValue={editTQC2010_R01?.itemNm || ''}
				editable={false}
				name="itemCd"
				handleChange={noopChange}
			/>
			<CodeForm
				label="작업자"
				value={editTQC2010_R01?.workEmp || ''}
				custNmValue={editTQC2010_R01?.workEmpNm || ''}
				editable={false}
				name="workEmp"
				handleChange={noopChange}
			/>
			<BRDiv />
			<DropDownForm
				title="검사구분"
				value={editTQC2010_R01?.inspectType || ''}
				name="inspectType"
				initLabelLess
				data={inspectionTypeList}
				editable={false}
				onChange={handleChange}
			/>
			<CodeForm
				label="검사자"
				value={editTQC2010_R01?.inspectorEmp || ''}
				custNmValue={editTQC2010_R01?.inspectorEmpNm || ''}
				editable
				name="inspectorEmp"
				handleFoucsId={handleInspectorFocusId}
				handleChange={getFieldHandlers('inspectorEmp').onChange}
				onDoubleClick={getFieldHandlers('inspectorEmp').onDoubleClick}
				onKeyDown={getFieldHandlers('inspectorEmp').onEnterKeyDown}
			/>
			<SingleDateForm
				title="검사일자"
				value={editTQC2010_R01?.inspectDt ? new Date(editTQC2010_R01.inspectDt) : undefined}
				setValue={(value) => {
					if (!editTQC2010_R01) return;
					setEditTQC2010_R01({ ...editTQC2010_R01, inspectDt: value ? formatDate(value) : undefined });
				}}
				editable
				visible={inspectDtVisible}
				setVisible={setInspectDtVisible}
				pickerPos={inspectDtPickerPos}
				setPickerPos={setInspectDtPickerPos}
				placeholder="검사일자 선택"
			/>
			<TimeForm
				title="검사시간"
				value={editTQC2010_R01?.inspectTime || ''}
				setValue={(timeValue) => {
					if (!editTQC2010_R01) return;
					setEditTQC2010_R01({ ...editTQC2010_R01, inspectTime: timeValue });
				}}
				editable
			/>
		</div>
	);
};
